import type { GameAction, PlayerObservationV1 } from "@fate-kingdom/engine";
import type { Ref } from "react";

import type { AlphaPendingAction } from "../AlphaActionPanels";
import { AlphaGameIcon } from "../icons/AlphaGameIcon";
import { getAlphaDisplayName } from "../presentation/alpha-card-presentation";

type DisbandAction = Extract<GameAction, { readonly type: "disband-army" | "disband-commander" }>;

function disbandCardName(observation: PlayerObservationV1, action: DisbandAction): string {
  const side = observation.lanes[action.laneId].own;
  const card =
    action.type === "disband-army"
      ? side.armies.find((army) => army.cardInstanceId === action.armyCardInstanceId)
      : side.commander;
  return card === null || card === undefined ? "field card" : getAlphaDisplayName(card);
}

export function DiscardZones({
  observation,
  legalActions,
  blocked,
  dropActive,
  dropHovered,
  ownTargetRef,
  onChoose,
}: {
  readonly observation: PlayerObservationV1;
  readonly legalActions: readonly GameAction[];
  readonly blocked: boolean;
  readonly dropActive: boolean;
  readonly dropHovered: boolean;
  readonly ownTargetRef: Ref<HTMLDetailsElement>;
  readonly onChoose: (pending: AlphaPendingAction) => void;
}) {
  const disbandActions = legalActions.filter(
    (action): action is DisbandAction =>
      action.type === "disband-army" || action.type === "disband-commander",
  );
  return (
    <aside className="alpha-discard-zones" aria-label="Discard piles">
      <div className="alpha-discard-zone alpha-discard-zone--opponent">
        <AlphaGameIcon name="discard" aria-hidden="true" />
        <span>
          Opponent discard <b>{observation.opponent.discard.length}</b>
        </span>
      </div>
      <details
        ref={ownTargetRef}
        className={`alpha-discard-zone alpha-discard-zone--own ${dropActive ? "is-drop-active" : ""} ${dropHovered ? "is-drop-hovered" : ""}`}
        data-alpha-discard-target="own"
        data-alpha-drop-state={dropHovered ? "hovered" : dropActive ? "legal" : "idle"}
      >
        <summary>
          <AlphaGameIcon name="discard" aria-hidden="true" />
          <span>
            {dropActive ? "Disband to discard" : "Your discard"}{" "}
            <b>{observation.own.discard.length}</b>
          </span>
        </summary>
        {disbandActions.length === 0 ? (
          <small>No field card can be disbanded now.</small>
        ) : (
          <div className="alpha-discard-zone__actions">
            {disbandActions.map((action) => {
              const name = disbandCardName(observation, action);
              return (
                <button
                  key={JSON.stringify(action)}
                  type="button"
                  disabled={blocked}
                  onClick={() =>
                    onChoose({
                      action,
                      title: `Disband ${name}?`,
                      description:
                        "Disband to discard with no resource refund. This does not consume the Deployment Action.",
                    })
                  }
                >
                  Disband {name} to discard
                </button>
              );
            })}
          </div>
        )}
      </details>
    </aside>
  );
}
